import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';

import { useCallback } from 'react';
import { SignApprovalType } from '../../../service/gen/esign';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { DetailsTextField, TitleTextField } from '../../components';

import { BackIconButton } from '../../components/BackIconButton';
import { getStackItemStyles } from '../../containers/CreateApproval';
import { EsignFileAttachment } from '../EsignFileAttachment';
import { RecipientList } from '../RecipientList';
import { InputState } from '../utils/forms';
import { CreateEsignApprovalComponentProps } from './CreateEsignApprovalComponentProps';

export const CreateEsignApprovalComponent: React.FC<CreateEsignApprovalComponentProps> = (
    props: CreateEsignApprovalComponentProps
): JSX.Element => {
    const {
        form,
        fileService,
        fileUploadAcceptedTypes,
        signApprovalType,
        customFooterComponent,
        onBack,
        onNext,
        stageViewEnabled,
    } = props;
    const host = getAppHost();
    const loc = host.getLocale();

    const updateListenerCallback = useCallback(
        (input: InputState<string>, errorMessage: string, newValue: string): void => {
            input.setValue(errorMessage ? '' : newValue);
            input.setErrorMessage(errorMessage);
        },
        []
    );

    const onDetailsFocus = (): void => {
        if (form.titleInput.value.trim().length === 0) {
            form.titleInput.setErrorMessage(loc.strings.required);
        }
    };

    return (
        <>
            <Stack>
                <Stack.Item styles={getStackItemStyles()}>
                    <TitleTextField
                        label={loc.strings.request_title_text}
                        onValueChange={(errorMessage: string, newValue: string): void =>
                            updateListenerCallback(form.titleInput, errorMessage, newValue)
                        }
                        placeholder={loc.strings.request_title_placeholder_text}
                        errorMessage={form.titleInput.errorMessage}
                        defaultValue={form.titleInput.value}
                        maxLength={form.titleInput.maxLength}
                        ariaLabel={loc.strings.request_title_text}
                        required
                    />
                </Stack.Item>

                <Stack.Item styles={getStackItemStyles()}>
                    <DetailsTextField
                        label={loc.strings.additional_details_text}
                        onValueChange={(errorMessage: string, newValue: string): void =>
                            updateListenerCallback(form.detailsInput, errorMessage, newValue)
                        }
                        focusListener={onDetailsFocus}
                        placeholder={loc.strings.additional_details_placeholder_text}
                        errorMessage={form.detailsInput.errorMessage}
                        defaultValue={form.detailsInput.value}
                        maxLength={form.detailsInput.maxLength}
                        ariaLabel={loc.strings.additional_details_aria_label}
                    />
                </Stack.Item>

                <Stack.Item styles={getStackItemStyles()}>
                    <EsignFileAttachment
                        fileService={fileService}
                        showLabel
                        accept={fileUploadAcceptedTypes}
                        files={form.filesInput.value}
                        onFilesUpdate={form.filesInput.setValue}
                        required
                        signApprovalType={signApprovalType}
                    />
                </Stack.Item>

                <Stack.Item styles={getStackItemStyles()}>
                    <RecipientList
                        signApprovalType={signApprovalType}
                        availableRoles={form.availableRoles}
                        recipientsInput={form.recipientsInput}
                        completeInOrderInput={form.completeInOrderInput}
                        required
                    />
                </Stack.Item>
            </Stack>

            <div className='modal-footer'>
                <Stack horizontal horizontalAlign='space-between'>
                    <div className='button-group'>
                        <BackIconButton
                            text={loc.strings.carousel_back}
                            ariaLabel={loc.strings.carousel_back}
                            onClick={onBack}
                        />
                        {customFooterComponent}
                    </div>
                    <div className='button-group'>
                        {!stageViewEnabled ? (
                            <PrimaryButton
                                text={loc.strings.send}
                                onClick={(): void => onNext(false)}
                                disabled={!form.isFormValid}
                                ariaLabel={loc.strings.send}
                            />
                        ) : signApprovalType === SignApprovalType.Adobe ? (
                            <>
                                <DefaultButton
                                    text={loc.strings.send}
                                    onClick={(): void => onNext(false)}
                                    disabled={!form.isFormValid}
                                    ariaLabel={loc.strings.send}
                                />
                                <PrimaryButton
                                    text={loc.strings.prepare}
                                    onClick={(): void => onNext(true)}
                                    disabled={!form.isFormValid}
                                    ariaLabel={loc.strings.prepare}
                                />
                            </>
                        ) : signApprovalType === SignApprovalType.Docusign ? (
                            <PrimaryButton
                                text={loc.strings.prepare}
                                onClick={(): void => onNext(true)}
                                disabled={!form.isFormValid}
                                ariaLabel={loc.strings.prepare}
                            />
                        ) : null}
                    </div>
                </Stack>
            </div>
        </>
    );
};
